import { useCallback } from 'react';

import {
  type AiBuilderChatEvent,
  type AiBuilderChatOrchestrationEvent,
  type AiBuilderChatQuestionSnapshotReplaceEvent,
  type AiBuilderMessagePatchEvent,
} from '../utils/types';

const delay = (ms: number) =>
  new Promise(resolve => {
    window.setTimeout(resolve, ms);
  });
import { type AiBuilderChatState } from './types';

type AiBuilderTurnRunnerEvent = AiBuilderChatOrchestrationEvent;

type AiBuilderTurnRunnerEventStream =
  | Iterable<AiBuilderTurnRunnerEvent>
  | AsyncIterable<AiBuilderTurnRunnerEvent>;

type UseAiBuilderTurnRunnerParams = {
  activeRunIdRef: {
    current: number;
  };
  updateState: (
    updater: (prev: AiBuilderChatState) => AiBuilderChatState,
  ) => void;
  requestComposerFocus: () => void;
  applyGeneratedQuizzes: (
    event: Extract<AiBuilderChatEvent, { type: 'applyGeneratedQuizzes' }>,
  ) => void;
  replaceQuestionSnapshots: (
    event: AiBuilderChatQuestionSnapshotReplaceEvent,
  ) => void;
  applyAiBuilderEvent: (event: AiBuilderMessagePatchEvent) => void;
  onAuthExpired: () => void;
};

export const useAiBuilderTurnRunner = ({
  activeRunIdRef,
  updateState,
  requestComposerFocus,
  applyGeneratedQuizzes,
  replaceQuestionSnapshots,
  applyAiBuilderEvent,
  onAuthExpired,
}: UseAiBuilderTurnRunnerParams) => {
  return useCallback(
    async (events: AiBuilderTurnRunnerEventStream) => {
      const runId = activeRunIdRef.current + 1;
      activeRunIdRef.current = runId;
      updateState(prev => ({
        ...prev,
        isGenerating: true,
        turnInProgress: true,
      }));

      try {
        for await (const event of events) {
          if (activeRunIdRef.current !== runId) return;

          if (event.type === 'delay') {
            await delay(event.ms);
            continue;
          }

          if (event.type === 'setSessionId') {
            updateState(prev =>
              prev.sessionId === event.sessionId
                ? prev
                : { ...prev, sessionId: event.sessionId },
            );
            continue;
          }

          if (event.type === 'setUsage') {
            updateState(prev => ({ ...prev, usage: event.usage }));
            continue;
          }

          if (event.type === 'authExpired') {
            // 오류 말풍선을 만들지 않는다 — 재로그인 모달이 안내를 담당한다.
            // 직전 유저 메시지의 '전송 실패' 표시는 dispatcher 가 이미 처리했다.
            onAuthExpired();
            continue;
          }

          if (event.type === 'applyGeneratedQuizzes') {
            applyGeneratedQuizzes(event);
            continue;
          }

          if (event.type === 'replaceQuestionSnapshots') {
            replaceQuestionSnapshots(event);
            continue;
          }

          applyAiBuilderEvent(event);
        }
      } finally {
        if (activeRunIdRef.current === runId) {
          updateState(prev => ({
            ...prev,
            isGenerating: false,
            turnInProgress: false,
          }));
          requestComposerFocus();
        }
      }
    },
    [
      activeRunIdRef,
      applyGeneratedQuizzes,
      replaceQuestionSnapshots,
      applyAiBuilderEvent,
      onAuthExpired,
      requestComposerFocus,
      updateState,
    ],
  );
};
